import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  noop,
  Normalizers,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  take,
  where,

} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "../../../../services/abstract.service";
import MaterialListAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/materials/material-list/material-list.entity";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { IFilters } from "../../filters/interfaces/filterInterface";

const apiUrl = env.API_MATERIAL_URL;
const moduleName = "material_list";

@Injectable({
  providedIn: "root",
})
export class MaterialListService extends AbstractService<MaterialListAttributes,any,any> {
    
    /** The last filter used to load the list */
    currentFilter: CombinedFilter<MaterialListAttributes>;

    constructor(
        http: HttpClient,
    ) {
        super(apiUrl, moduleName, http);
    }

    findByFilter(filter: CombinedFilter<MaterialListAttributes>, options?: RequestOptions) {
      const API_URL: string =
        apiUrl +
        `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(filter))}`;
        const headers: Record<string, any> = {};
        // see order-customer-export for more info about this header
        // ctx: stands for context
        if (options?.contextHeader) headers['x-ave-ctx'] = options.contextHeader;
        return this.http.get<IPaginationResult<MaterialListAttributes>>(API_URL, { headers });
    }

    /**
     * Obtiene listas de materiales con paginación y filtros.
     *
     * Estrategia de búsqueda:
     * - Sin búsqueda: Un solo objeto con filtros base
     * - Con búsqueda: Dos objetos (OR lógico) - busca en 'code', 'name'
     *
     * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
     *
     * @param options Pagination options
     * @param dataToFilter Filters to apply
     * @returns Paginated data
     *
     * Search fields: code, name
     * Array filters: statusId
     */
    findAllWithCount(
        options: IPagination<MaterialListAttributes>,
        dataToFilter?: IFilters
    ): Observable<IPaginationResult<MaterialListAttributes>> {

        const hasSearch = !!options.search;

        /**
         * Construye las condiciones base que SIEMPRE deben aplicarse.
         * Incluye: _deleted=false, filtro de estados.
         *
         * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
         */
        const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<MaterialListAttributes>[] => {
          const deletedFilter: FilterFn<MaterialListAttributes> = params.useOrWhere
            ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
            : where('_deleted', Ops.eq(false));   // Primer objeto del array

          return [
            deletedFilter,
            dataToFilter?.status && dataToFilter.status.length > 0
              ? andWhere('statusId', Ops.in(...dataToFilter.status))
              : noop()
          ];
        };

        // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

        // Objeto 1: Condiciones base + búsqueda por CODE (o solo condiciones si no hay búsqueda)
        const searchByCodeQueryObject: FilterFn<MaterialListAttributes>[] = [
          ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
          hasSearch ? andWhere("code", Ops.like(options.search!, "i")) : noop()
        ];

        // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
        const searchByNameQueryObject: FilterFn<MaterialListAttributes>[] = hasSearch ? [
          ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
          andWhere("name", Ops.like(options.search!, "i"))
        ] : [];

        // ========== ENSAMBLAJE FINAL ==========

        const query = buildQuery<MaterialListAttributes>(
          // Expandimos ambos objetos en el buildQuery
          ...searchByCodeQueryObject,
          ...searchByNameQueryObject,

          // Ordenamiento y paginación
          orderBy(
            options.sort ?? "code",
            options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
          ),
          page(options.currentPage ?? 1, options.limitPerPage ?? 100)
        );

        this.currentFilter = query;

        const API_URL: string =
        apiUrl +
        `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
        return this.http.get<IPaginationResult<MaterialListAttributes>>(API_URL);
    }

    /**
     * Find to combo box
     * @returns Data
     */
    findToComboBox(): Observable<IRequestResult<MaterialListAttributes[]>> {
        const query = buildQuery<MaterialListAttributes>(
            where("statusId", StatusEnum.ACTIVE),
            select(["code", "name"]),
            orderBy("code", OrderVariant.Asc)
        );

        const API_URL: string =
        apiUrl +
        `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
        return this.http.get<IRequestResult<MaterialListAttributes[]>>(API_URL);
    }

    importFile(data: FormData){
        const API_URL: string = apiUrl + `api/${moduleName}/upload`;
        return this.http.post(API_URL, data);
    }

    getFileTemplateMaterialList(): string {
    return env.MATERIAL_LIST_LOAD_TEMPLATE || null;
  }
}
